import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { isLexUser } from "@/utils/lexDomain";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import UpgradeModal from "@/components/UpgradeModal";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { resetAreaGuides } from "@/components/AreaGuide";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building, Upload, X, Download, Trash2, Lock, Shield, User, CreditCard, Calculator, Mail, Briefcase, UserPlus, Link2, Copy, CheckCircle2, Eye, EyeOff, ToggleLeft, ToggleRight, FileText, Clock, Truck, Palette, AlertTriangle } from "lucide-react";
import Migration from "@/pages/Migration";
import SubscriptionManagement from "@/components/SubscriptionManagement";
import { AiConsentToggle, BankAiConsentToggle } from "@/components/AiConsentDialog";
import InboundEmailSection from "@/components/InboundEmailSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TaxSettings, CountryInfo } from "./settings/TaxSettings";
import { AdvisorAccessSection } from "./settings/AdvisorAccessSection";
import NumberRangesSection from "./settings/NumberRangesSection";
import InvoiceDesignSection from "./settings/InvoiceDesignSection";
import LexStammdatenTab from "@/components/lex/LexStammdatenTab";
import AVVSection from "@/components/AVVSection";
import { TaxProfileForm } from "@/components/TaxProfileForm";
import TaxHealthScoreCard from "@/components/TaxHealthScoreCard";
// Feb 2026 — DACH-Smart-Inputs (Phase 3)
import { IBANInput, VATInput, PhoneInput, PostcodeInput } from "@/components/inputs/SmartInputs";
import axios from "axios";
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline status indicator component
const SaveStatus = ({ fieldName, saveStatus }) => {
  const status = saveStatus[fieldName];
  if (!status) return null;

  if (status === 'saving') {
    return <span className="text-xs text-gray-500 ml-2">Speichert...</span>;
  }
  if (status === 'saved') {
    return <span className="text-xs text-green-600 ml-2">✓ Gespeichert</span>;
  }
  if (status === 'error') {
    return <span className="text-xs text-red-600 ml-2">✗ Fehler</span>;
  }
  return null;
};

export default function Settings() {
  const { t } = useTranslation();
  const { user, logout, refreshUser } = useAuth();
  // lex.ivoliq: reduzierte Einstellungen + Navy/Gold-Design (siehe /app/design_guidelines_lex.json)
  const isLex = isLexUser(user);
  const navigate = useNavigate();

  // P2 Settings-Deep-Links (Feb 2026): unterstützt ?tab=advisor und
  // ?section=banking-ai (mit Auto-Scroll). Beispiel:
  //   /settings?tab=company&section=banking-ai
  // Sections müssen ein Element mit id="section-{name}" rendern.
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionFromUrl = searchParams.get('section');
  const SECTION_TO_TAB = {
    'banking-ai': 'company',
    'ocr-ai': 'company',
    'avv': 'plan',
    'tax-profile': 'taxes',
  };
  const initialTab = searchParams.get('tab')
    || (sectionFromUrl ? SECTION_TO_TAB[sectionFromUrl] : null)
    || 'profile';
  const [activeTab, setActiveTab] = useState(initialTab);

  // In-App-Navigation auf ?tab=… (z. B. „Jetzt upgraden“ aus dem Design-Teaser) übernehmen
  const urlTab = searchParams.get('tab');
  useEffect(() => {
    if (urlTab && urlTab !== activeTab) setActiveTab(urlTab);
  // eslint-disable-next-line
  }, [urlTab]);

  // lex: ausgeblendete Tabs nicht per Deep-Link erreichbar
  useEffect(() => {
    if (isLex && ["taxes", "advisor", "migration"].includes(activeTab)) setActiveTab("profile");
  }, [isLex, activeTab]);

  // URL aktuell halten, ohne Browser-History-Spam (replace).
  useEffect(() => {
    const current = searchParams.get('tab');
    if (current !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line
  }, [activeTab]);

  // Section-Auto-Scroll, sobald der richtige Tab gerendert ist.
  useEffect(() => {
    if (!sectionFromUrl) return;
    const targetTab = SECTION_TO_TAB[sectionFromUrl];
    if (targetTab && targetTab !== activeTab) return;  // wait until tab switch
    const tid = setTimeout(() => {
      const el = document.getElementById(`section-${sectionFromUrl}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('ring-2', 'ring-emerald-300', 'ring-offset-2');
        setTimeout(() => el.classList.remove('ring-2', 'ring-emerald-300', 'ring-offset-2'), 2000);
      }
    }, 350);
    return () => clearTimeout(tid);
  // eslint-disable-next-line
  }, [sectionFromUrl, activeTab]);

  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    company_name: "",
    owner_name: "",
    address: "",
    street: "",
    street_number: "",
    postal_code: "",
    city: "",
    tax_id: "",
    che_number: "",
    company_register_number: "",
    phone: "",
    email: "",
    website: "",
    bank_name: "",
    iban: "",
    bic: "",
    svnr: "",
    birth_date: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({}); // Track save status per field
  const [originalFormData, setOriginalFormData] = useState({});
  
  // Email settings state (Increment 3)
  const [emailSettings, setEmailSettings] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchEmailSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
      const initialData = {
        company_name: response.data.company_name || "",
        owner_name: response.data.owner_name || "",
        address: response.data.address || "",
        street: response.data.street || "",
        street_number: response.data.street_number || "",
        postal_code: response.data.postal_code || "",
        city: response.data.city || "",
        tax_id: response.data.tax_id || "",
        che_number: response.data.che_number || "",
        company_register_number: response.data.company_register_number || "",
        phone: response.data.phone || "",
        email: response.data.email || "",
        website: response.data.website || "",
        bank_name: response.data.bank_name || "",
        iban: response.data.iban || "",
        bic: response.data.bic || "",
        svnr: response.data.svnr || "",
        birth_date: response.data.birth_date || "",
      };
      setFormData(initialData);
      setOriginalFormData(initialData);
      // P0 hotfix: Use base64 first for reliable preview, fallback to logo_url
      if (response.data.logo_base64) {
        // Determine MIME type from base64 content or use generic image type
        const mimeType = response.data.logo_url?.includes('.png') ? 'image/png' : 
                        response.data.logo_url?.includes('.jpg') || response.data.logo_url?.includes('.jpeg') ? 'image/jpeg' :
                        response.data.logo_url?.includes('.svg') ? 'image/svg+xml' : 'image/png';
        setLogoPreview(`data:${mimeType};base64,${response.data.logo_base64}`);
      } else if (response.data.logo_url) {
        // Fallback to logo_url for backward compatibility
        const logoUrl = response.data.logo_url.startsWith('http') 
          ? response.data.logo_url 
          : `${process.env.REACT_APP_BACKEND_URL}${response.data.logo_url}`;
        setLogoPreview(logoUrl);
      } else {
        // Clear logo preview if no logo
        setLogoPreview(null);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  // Email settings functions (Increment 3)
  const fetchEmailSettings = async () => {
    try {
      const response = await api.get('/user/email-settings');
      setEmailSettings(response.data);
    } catch (error) {
      console.error("Error fetching email settings:", error);
    }
  };

  const handleSenderModeChange = async (newMode) => {
    if (newMode === emailSettings?.sender_mode) return;
    
    setEmailLoading(true);
    try {
      await api.put('/user/email-settings', { sender_mode: newMode });
      await fetchEmailSettings();
      toast.success(t('settings.emailSettingsUpdated', 'Email settings updated successfully'));
    } catch (error) {
      console.error("Error updating sender mode:", error);
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response?.data?.detail || t('settings.googleOAuthRequiresPro', 'Google OAuth email sending requires Pro plan or higher'));
        setUpgradeModalOpen(true);
      } else if (error.response?.status === 400) {
        toast.error(t('settings.googleNotConnected', 'Google email not connected. Please connect first.'));
      } else {
        toast.error(t('settings.emailSettingsError', 'Failed to update email settings'));
      }
    } finally {
      setEmailLoading(false);
    }
  };

  const handleConnectGoogle = async () => {
    setEmailLoading(true);
    try {
      const response = await api.get('/user/google-email/auth-url');
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error("Error getting Google auth URL:", error);
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response?.data?.detail || t('settings.googleOAuthRequiresPro', 'Google OAuth email sending requires Pro plan or higher'));
        setUpgradeModalOpen(true);
      } else {
        toast.error(t('settings.googleConnectError', 'Failed to connect Google email'));
      }
      setEmailLoading(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    setEmailLoading(true);
    try {
      await api.post('/user/google-email/disconnect');
      await fetchEmailSettings();
      toast.success(t('settings.googleDisconnected', 'Google email disconnected successfully'));
    } catch (error) {
      console.error("Error disconnecting Google:", error);
      toast.error(t('settings.googleDisconnectError', 'Failed to disconnect Google email'));
    } finally {
      setEmailLoading(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error(t('settings.logoTooLarge', 'Logo zu groß. Maximum: 5 MB'));
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload immediately
      setLogoUploading(true);
      try {
        const formDataLogo = new FormData();
        formDataLogo.append("file", file);
        await api.post(`/settings/upload-logo`, formDataLogo, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // Logo is now saved, no need for additional action
      } catch (error) {
        console.error("Error uploading logo:", error);
        toast.error(t('settings.logoUploadError', 'Fehler beim Hochladen des Logos'));
        // Reset preview on error
        setLogoPreview(null);
        setLogoFile(null);
      } finally {
        setLogoUploading(false);
      }
    }
  };

  // Autosave function for individual fields
  const autosaveField = async (fieldName, value) => {
    // Only save if value has actually changed
    if (originalFormData[fieldName] === value) {
      return;
    }

    try {
      setSaveStatus(prev => ({ ...prev, [fieldName]: 'saving' }));
      
      const updateData = { [fieldName]: value };
      await api.put(`/settings`, updateData);
      
      // Update original data to reflect saved state
      setOriginalFormData(prev => ({ ...prev, [fieldName]: value }));
      
      // Show success status briefly
      setSaveStatus(prev => ({ ...prev, [fieldName]: 'saved' }));
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, [fieldName]: null }));
      }, 2000);
      
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSaveStatus(prev => ({ ...prev, [fieldName]: 'error' }));
      toast.error(t('settings.saveError', 'Fehler beim Speichern'));
      
      // Clear error status after a delay
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, [fieldName]: null }));
      }, 3000);
    }
  };

  // Handle blur events for text inputs
  const handleFieldBlur = (fieldName, value) => {
    autosaveField(fieldName, value);
  };

  // Legacy submit handler - now mostly unused due to autosave
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Form submission is now handled by individual field autosave
    // This is kept for any remaining edge cases
  };

  const handleDataExport = async () => {
    try {
      const response = await api.get('/user/export-data');
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ivoliq-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(t('settings.dataExported'));
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error(t('settings.exportError'));
    }
  };

  // Change Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  const handleChangePassword = async () => {
    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error(t('settings.fillAllPasswordFields', 'Bitte füllen Sie alle Felder aus'));
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('settings.passwordsDontMatch', 'Neue Passwörter stimmen nicht überein'));
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error(t('settings.passwordTooShort', 'Neues Passwort muss mindestens 8 Zeichen haben'));
      return;
    }

    try {
      await api.post('/auth/change-password', null, {
        params: {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        }
      });
      toast.success(t('settings.passwordChanged', 'Passwort erfolgreich geändert'));
      setPasswordDialogOpen(false);
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      // Logout and redirect to login after password change
      setTimeout(() => {
        logout();
        navigate(isLex ? '/lex-login' : '/login');
      }, 2000);
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.status === 401) {
        toast.error(t('settings.currentPasswordIncorrect', 'Aktuelles Passwort ist falsch'));
      } else if (error.response?.status === 400 && error.response?.data?.detail?.includes('OAuth')) {
        toast.error(t('settings.cannotChangeOAuthPassword', 'Passwort kann für OAuth-Accounts nicht geändert werden'));
      } else {
        toast.error(t('settings.passwordChangeError', 'Fehler beim Ändern des Passworts'));
      }
    }
  };

  // Delete Account State
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Sicherheit & Sitzungen (Juni 2026)
  const [loginHistory, setLoginHistory] = useState([]);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);
  useEffect(() => {
    api.get('/auth/login-history').then(r => setLoginHistory(r.data.events || [])).catch(() => {});
  }, []);
  const handleLogoutAllDevices = async () => {
    setLogoutAllLoading(true);
    try {
      await api.post('/auth/logout-all-devices', {});
      toast.success(t('settings.loggedOutAllDevices', 'Auf allen Geräten abgemeldet'));
      setTimeout(() => { logout(); navigate(isLex ? '/lex-login' : '/login'); }, 1200);
    } catch (e) {
      toast.error(t('settings.logoutAllError', 'Abmelden fehlgeschlagen'));
      setLogoutAllLoading(false);
    }
  };
  const formatDevice = (ua = '') => {
    let browser = 'Browser';
    if (/Edg/i.test(ua)) browser = 'Edge';
    else if (/Chrome/i.test(ua)) browser = 'Chrome';
    else if (/Firefox/i.test(ua)) browser = 'Firefox';
    else if (/Safari/i.test(ua)) browser = 'Safari';
    let os = '';
    if (/Windows/i.test(ua)) os = 'Windows';
    else if (/Macintosh|Mac OS/i.test(ua)) os = 'macOS';
    else if (/Android/i.test(ua)) os = 'Android';
    else if (/iPhone|iPad|iOS/i.test(ua)) os = 'iOS';
    else if (/Linux/i.test(ua)) os = 'Linux';
    return os ? `${browser} · ${os}` : browser;
  };
  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return iso; }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error(t('settings.passwordRequired', 'Passwort erforderlich'));
      return;
    }

    try {
      await api.post('/auth/delete-account', { password: deletePassword, reason: deleteReason || null });
      toast.success(t('settings.accountDeleted', 'Account wurde erfolgreich gelöscht'));
      setDeleteDialogOpen(false);
      setTimeout(() => {
        logout();
        navigate(isLex ? '/lex-login' : '/login');
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.response?.status === 401) {
        toast.error(t('settings.invalidPassword', 'Falsches Passwort'));
      } else {
        toast.error(t('settings.deleteError', 'Fehler beim Löschen des Accounts'));
      }
    }
  };

  const removeLogo = async () => {
    try {
      // Delete the logo using the dedicated endpoint
      await api.delete('/settings/logo');
      
      // Clear local state
      setLogoFile(null);
      setLogoPreview(null);
      
      toast.success(t('settings.logoRemoved', 'Logo entfernt'));
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error(t('settings.logoRemoveError', 'Fehler beim Entfernen des Logos'));
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {isLex ? (
        <div>
          <div className="lex-gold-line mb-4" />
          <h1 className="lex-serif text-4xl sm:text-5xl font-semibold" style={{ color: "#0A1128" }}>Einstellungen.</h1>
          <p className="text-sm text-slate-500 mt-2">Profil, Funktionen und Konto · alles Steuerliche übernimmt Ihre Kanzlei.</p>
        </div>
      ) : (
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>{t("settings.title")}</h1>
        <p className="text-sm text-slate-500">{t("settings.subtitle")}</p>
      </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="settings-tabs">
        <TabsList className={`w-full justify-start h-auto flex-wrap ${isLex ? "bg-white border border-slate-200 rounded-none p-1" : "bg-slate-100 p-1 rounded-xl"}`}>
          <TabsTrigger value="profile" className={`gap-1.5 text-xs sm:text-sm px-3 py-2 ${isLex ? "rounded-none data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" : "rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"}`} data-testid="settings-tab-profile">
            <User className="h-3.5 w-3.5" />
            Profil
          </TabsTrigger>
          <TabsTrigger value="company" className={`gap-1.5 text-xs sm:text-sm px-3 py-2 ${isLex ? "rounded-none data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" : "rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"}`} data-testid="settings-tab-company">
            <Building className="h-3.5 w-3.5" />
            {isLex ? "Stammdaten" : "Funktionen"}
          </TabsTrigger>
          <TabsTrigger value="design" className={`gap-1.5 text-xs sm:text-sm px-3 py-2 ${isLex ? "rounded-none data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" : "rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"}`} data-testid="settings-tab-design">
            <Palette className="h-3.5 w-3.5" />
            {isLex ? "Honorarnote" : "Rechnungsdesign"}
          </TabsTrigger>
          {!isLex && (
          <TabsTrigger value="taxes" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm px-3 py-2" data-testid="settings-tab-taxes">
            <Calculator className="h-3.5 w-3.5" />
            Steuern
          </TabsTrigger>
          )}
          <TabsTrigger value="plan" className={`gap-1.5 text-xs sm:text-sm px-3 py-2 ${isLex ? "rounded-none data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" : "rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"}`} data-testid="settings-tab-plan">
            <CreditCard className="h-3.5 w-3.5" />
            {isLex ? "Konto" : "Plan & Konto"}
          </TabsTrigger>
          {!isLex && (
          <TabsTrigger value="advisor" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm px-3 py-2" data-testid="settings-tab-advisor">
            <Briefcase className="h-3.5 w-3.5" />
            Steuerberater
          </TabsTrigger>
          )}
          {!isLex && (
          <TabsTrigger value="migration" className="rounded-lg gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm px-3 py-2" data-testid="settings-tab-migration">
            <Truck className="h-3.5 w-3.5" />
            Umzug
          </TabsTrigger>
          )}
        </TabsList>

        {/* ─── Tab: Profil ─── */}
        <TabsContent value="profile" className="space-y-6 mt-6" data-testid="settings-content-profile">
      <Card data-testid="settings-product-tour-card">
        <CardHeader>
          <CardTitle>Produkttour</CardTitle>
          <CardDescription>Die kurzen Bereichs-Einführungen erneut anzeigen.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            onClick={() => { resetAreaGuides(); toast.success("Produkttour zurückgesetzt — die Einführungen erscheinen wieder."); }}
            data-testid="settings-reset-tour-btn"
          >
            Produkttour erneut starten
          </Button>
        </CardContent>
      </Card>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo Upload */}
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.logo")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-4">
              {logoPreview ? (
                <div className="relative">
                  <img
                    src={logoPreview}
                    alt="Logo"
                    className="h-24 w-24 object-contain border-2 border-gray-200 rounded-lg"
                    onError={(e) => {
                      console.error('Logo preview failed to load:', logoPreview);
                      setLogoPreview(null);
                      toast.error(t('settings.logoLoadError', 'Logo konnte nicht geladen werden'));
                    }}
                  />
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    disabled={logoUploading}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="h-24 w-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                  <Building className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div className="flex-1">
                <Label htmlFor="logo-upload" className="cursor-pointer">
                  <div className={`inline-flex items-center space-x-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors text-sm font-medium ${logoUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <Upload className="h-4 w-4" />
                    <span>{logoUploading ? 'Lädt hoch...' : t("settings.uploadLogo")}</span>
                  </div>
                </Label>
                <Input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                  disabled={logoUploading}
                />
                <p className="text-xs text-gray-500 mt-2">
                  PNG, JPG oder SVG (empfohlen: 200x200px)
                </p>
                <p className="text-xs text-gray-600 mt-1 font-medium">
                  Max. 5 MB
                </p>
                {logoUploading && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-emerald-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Info */}
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.companyInfo")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("settings.companyName")} <SaveStatus fieldName="company_name" saveStatus={saveStatus} /></Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  onBlur={(e) => handleFieldBlur('company_name', e.target.value)}
                  placeholder="Meine Firma GmbH"
                />
              </div>
              <div>
                <Label>{t("settings.ownerName")} <SaveStatus fieldName="owner_name" saveStatus={saveStatus} /></Label>
                <Input
                  value={formData.owner_name}
                  onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                  onBlur={(e) => handleFieldBlur('owner_name', e.target.value)}
                  placeholder="Max Mustermann"
                />
              </div>
            </div>

            <div>
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-700">{t('settings.address', 'Address')}</p>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className={isLex ? "col-span-3" : "col-span-2"}>
                    <Label htmlFor="street">{isLex ? "Straße & Hausnummer" : t('customers.street', 'Street')} <SaveStatus fieldName="street" saveStatus={saveStatus} /></Label>
                    <Input
                      id="street"
                      placeholder={isLex ? "z. B. Opernring 3" : t('customers.streetPlaceholder', 'Street name')}
                      value={formData.street}
                      onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                      onBlur={(e) => handleFieldBlur('street', e.target.value)}
                    />
                  </div>
                  {!isLex && (
                  <div>
                    <Label htmlFor="street_number">{t('customers.streetNumber', 'No.')} <SaveStatus fieldName="street_number" saveStatus={saveStatus} /></Label>
                    <Input
                      id="street_number"
                      placeholder="123"
                      value={formData.street_number}
                      onChange={(e) => setFormData({ ...formData, street_number: e.target.value })}
                      onBlur={(e) => handleFieldBlur('street_number', e.target.value)}
                    />
                  </div>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="postal_code">{t('customers.postalCode', 'ZIP')} <SaveStatus fieldName="postal_code" saveStatus={saveStatus} /></Label>
                    <PostcodeInput
                      country={user?.country || 'DE'}
                      value={formData.postal_code}
                      onChange={(v) => setFormData({ ...formData, postal_code: v })}
                      onBlur={(e) => handleFieldBlur('postal_code', e.target.value)}
                      onCityFound={(city) => {
                        // Feb 2026 — Smart-Suggestion: PLZ-Lookup setzt city auto
                        if (!formData.city) {
                          setFormData((prev) => ({ ...prev, city }));
                          handleFieldBlur('city', city);
                        }
                      }}
                      lookupCity
                      label={null}
                      data-testid="settings-postal-code"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="city">{t('customers.city', 'City')} <SaveStatus fieldName="city" saveStatus={saveStatus} /></Label>
                    <Input
                      id="city"
                      placeholder={t('customers.cityPlaceholder', 'City name')}
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      onBlur={(e) => handleFieldBlur('city', e.target.value)}
                    />
                  </div>
                </div>

                {!isLex && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-xs text-yellow-800">
                    ⚠️ <strong>{t('settings.structuredAddressRequired', 'Swiss Compliance:')}</strong> {t('settings.structuredAddressNote', 'Structured address is mandatory for Swiss QR invoices from 21.11.2025. Please enter street and number separately.')}
                  </p>
                </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                {/* Germany & Austria: Tax ID / USt-IdNr. */}
                {user?.country === "DE" && (
                  <div>
                    <Label>Steuernummer / USt-IdNr. <SaveStatus fieldName="tax_id" saveStatus={saveStatus} /></Label>
                    <VATInput
                      country="DE"
                      value={formData.tax_id}
                      onChange={(v) => setFormData({ ...formData, tax_id: v })}
                      onBlur={(e) => handleFieldBlur('tax_id', e.target.value)}
                      label={null}
                      data-testid="settings-tax-id-de"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ihre Steuernummer oder Umsatzsteuer-Identifikationsnummer (USt-IdNr.)
                    </p>
                    {!(formData.tax_id && String(formData.tax_id).trim()) && (
                      <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2" data-testid="settings-tax-id-nudge">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                        <span className="text-xs text-amber-800">Hinterlege deine USt-IdNr. oder Steuernummer – sie ist auf jeder regulären Rechnung Pflicht und wird dort automatisch ausgewiesen.</span>
                      </div>
                    )}
                  </div>
                )}
                
                {user?.country === "AT" && (
                  <>
                    <div>
                      <Label>UID-Nummer (Umsatzsteuer-Identifikationsnummer) <SaveStatus fieldName="tax_id" saveStatus={saveStatus} /></Label>
                      <VATInput
                        country="AT"
                        value={formData.tax_id}
                        onChange={(v) => setFormData({ ...formData, tax_id: v })}
                        onBlur={(e) => handleFieldBlur('tax_id', e.target.value)}
                        label={null}
                        data-testid="settings-tax-id-at"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Ihre österreichische UID-Nummer (z.B. ATU12345678)
                      </p>
                      {!(formData.tax_id && String(formData.tax_id).trim()) && (
                        <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2" data-testid="settings-tax-id-nudge">
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                          <span className="text-xs text-amber-800">Hinterlege deine UID oder Steuernummer – sie ist auf jeder regulären Rechnung Pflicht und wird dort automatisch ausgewiesen.</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Firmenbuchnummer (optional) <SaveStatus fieldName="company_register_number" saveStatus={saveStatus} /></Label>
                      <Input
                        value={formData.company_register_number}
                        onChange={(e) => setFormData({ ...formData, company_register_number: e.target.value })}
                        onBlur={(e) => handleFieldBlur('company_register_number', e.target.value)}
                        placeholder="FN 123456a"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Ihre Firmenbuchnummer beim österreichischen Firmenbuch
                      </p>
                    </div>
                    {isLex && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>SVNr <SaveStatus fieldName="svnr" saveStatus={saveStatus} /></Label>
                          <Input
                            value={formData.svnr}
                            onChange={(e) => setFormData({ ...formData, svnr: e.target.value })}
                            onBlur={(e) => handleFieldBlur('svnr', e.target.value)}
                            placeholder="1234 010180"
                            data-testid="settings-svnr"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Für die FinanzOnline-Vollmacht Ihrer Steuerberatung
                          </p>
                        </div>
                        <div>
                          <Label>Geburtsdatum <SaveStatus fieldName="birth_date" saveStatus={saveStatus} /></Label>
                          <Input
                            type="date"
                            value={formData.birth_date}
                            onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                            onBlur={(e) => handleFieldBlur('birth_date', e.target.value)}
                            data-testid="settings-birth-date"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {user?.country === "CH" && (
                  <div>
                    <Label>CHE-Nummer (Unternehmens-Identifikationsnummer) <SaveStatus fieldName="che_number" saveStatus={saveStatus} /></Label>
                    <VATInput
                      country="CH"
                      value={formData.che_number}
                      onChange={(v) => setFormData({ ...formData, che_number: v })}
                      onBlur={(e) => handleFieldBlur('che_number', e.target.value)}
                      label={null}
                      data-testid="settings-che-number"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ihre CHE-Nummer mit MWST-Zusatz (z.B. CHE-123.456.789 MWST)
                    </p>
                  </div>
                )}
                
                {!user?.country || user?.country === "OTHER" && (
                  <div>
                    <Label>{t("settings.taxId")} <SaveStatus fieldName="tax_id" saveStatus={saveStatus} /></Label>
                    <Input
                      value={formData.tax_id}
                      onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                      onBlur={(e) => handleFieldBlur('tax_id', e.target.value)}
                      placeholder="Tax ID / VAT Number"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('settings.taxIdHelper', 'Your tax identification number or VAT number')}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>{t("settings.phone")} <SaveStatus fieldName="phone" saveStatus={saveStatus} /></Label>
                  <PhoneInput
                    country={user?.country || 'DE'}
                    value={formData.phone}
                    onChange={(v) => setFormData({ ...formData, phone: v })}
                    onBlur={(e) => handleFieldBlur('phone', e.target.value)}
                    label={null}
                    data-testid="settings-phone"
                  />
                </div>
                <div>
                  <Label>{t("settings.email")} <SaveStatus fieldName="email" saveStatus={saveStatus} /></Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onBlur={(e) => handleFieldBlur('email', e.target.value)}
                    placeholder="kontakt@firma.de"
                  />
                </div>
                <div>
                  <Label>{t("settings.website")} <SaveStatus fieldName="website" saveStatus={saveStatus} /></Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    onBlur={(e) => handleFieldBlur('website', e.target.value)}
                    placeholder="www.firma.de"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bank Info */}
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.bankInfo")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t("settings.bankName")} <SaveStatus fieldName="bank_name" saveStatus={saveStatus} /></Label>
              <Input
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                onBlur={(e) => handleFieldBlur('bank_name', e.target.value)}
                placeholder="Beispielbank"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("settings.iban")} <SaveStatus fieldName="iban" saveStatus={saveStatus} /></Label>
                <IBANInput
                  country={user?.country || 'DE'}
                  value={formData.iban}
                  onChange={(normalized) => setFormData({ ...formData, iban: normalized })}
                  onBlur={(e) => handleFieldBlur('iban', e.target.value)}
                  onBankFound={(name) => {
                    // Feb 2026 — Smart-Suggestion: IBAN-Lookup setzt bank_name auto
                    if (!formData.bank_name) {
                      setFormData((prev) => ({ ...prev, bank_name: name }));
                      handleFieldBlur('bank_name', name);
                    }
                  }}
                  lookupBank
                  label={null}
                  data-testid="settings-iban"
                />
              </div>
              <div>
                <Label>{t("settings.bic")} <SaveStatus fieldName="bic" saveStatus={saveStatus} /></Label>
                <Input
                  value={formData.bic}
                  onChange={(e) => setFormData({ ...formData, bic: e.target.value })}
                  onBlur={(e) => handleFieldBlur('bic', e.target.value)}
                  placeholder="COBADEFFXXX"
                />
              </div>
            </div>
          </CardContent>
        </Card>

      </form>
        </TabsContent>

        {/* ─── Tab: Firma ─── */}
        <TabsContent value="company" className="space-y-6 mt-6" data-testid="settings-content-company">

      {/* lex: Stammdaten — Kanzlei (Empfängerin) + Vollmachten */}
      {isLex && <LexStammdatenTab />}

      {/* Email Settings - outside form (lex: entfällt, Versand läuft fix über ivoliq Mail) */}
      {!isLex && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            📧 {t('settings.emailSettings', 'Email Settings')}
          </CardTitle>
          <CardDescription>
            {t('settings.emailSettingsDesc', 'Configure how invoices are sent via email')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {emailSettings && (
            <>
              {/* Usage Display */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">{t('settings.emailUsage', 'Monthly Email Usage')}</h4>
                {(emailSettings.monthly_limit === null || emailSettings.monthly_limit === undefined) ? (
                  <span className="text-sm text-gray-600">
                    {t('settings.unlimitedEmails', 'Unbegrenzte E-Mails (Pro Plan)')}
                  </span>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        {emailSettings.current_usage} / {emailSettings.monthly_limit} {t('settings.emailsSent', 'emails sent')}
                      </span>
                      <span className="text-xs text-gray-500">
                        {t('settings.resetsOn', 'Resets on')}: {emailSettings.reset_date}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div 
                        className="bg-emerald-600 h-2 rounded-full" 
                        style={{ width: `${Math.min((emailSettings.current_usage / emailSettings.monthly_limit) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </>
                )}
              </div>

              {/* Sender Mode Selection */}
              <div>
                <h4 className="font-medium mb-3">{t('settings.emailProvider', 'Email Provider')}</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="brevo"
                      name="sender_mode"
                      value="brevo"
                      checked={emailSettings.sender_mode === 'brevo'}
                      onChange={() => handleSenderModeChange('brevo')}
                      disabled={emailLoading}
                      className="h-4 w-4 text-emerald-600"
                    />
                    <label htmlFor="brevo" className="flex-1">
                      <div className="font-medium">ivoliq Mail (Standard)</div>
                      <div className="text-sm text-gray-500">
                        {t('settings.brevoDesc', 'Reliable email delivery via ivoliq servers')}
                      </div>
                    </label>
                  </div>
                  
                  {/* Gmail option - only show if feature is enabled */}
                  {emailSettings.gmail_oauth_enabled && (
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        id="google_oauth"
                        name="sender_mode"
                        value="google_oauth"
                        checked={emailSettings.sender_mode === 'google_oauth'}
                        onChange={() => handleSenderModeChange('google_oauth')}
                        disabled={emailLoading || !emailSettings.google_connected}
                        className="h-4 w-4 text-emerald-600"
                      />
                      <label htmlFor="google_oauth" className="flex-1">
                        <div className="font-medium flex items-center gap-2">
                          Gmail (Pro Plan)
                          {!emailSettings.google_connected && (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                              {t('settings.notConnected', 'Not Connected')}
                            </span>
                          )}
                          {emailSettings.google_connected && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              {t('settings.connected', 'Connected')}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {t('settings.gmailDesc', 'Send emails directly from your Gmail account')}
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Google Connection Management - only show if feature is enabled */}
              {emailSettings.gmail_oauth_enabled && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">{t('settings.googleConnection', 'Google Connection')}</h4>
                  {emailSettings.google_connected ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-green-600">
                          ✓ {t('settings.googleConnected', 'Google email connected')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {t('settings.googleConnectedDesc', 'You can send emails via your Gmail account')}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDisconnectGoogle}
                        disabled={emailLoading}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        {emailLoading ? t('common.loading', 'Loading...') : t('settings.disconnect', 'Disconnect')}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-600">
                          {t('settings.googleNotConnected', 'Google email not connected')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {t('settings.googleNotConnectedDesc', 'Connect to send emails via Gmail (Pro plan required)')}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleConnectGoogle}
                        disabled={emailLoading}
                        className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      >
                        {emailLoading ? t('common.loading', 'Loading...') : t('settings.connect', 'Connect')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Datenschutz & KI - moved to Funktionen tab (lex: entfällt) */}
      {!isLex && (
      <Card id="section-banking-ai" className="transition-all">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            Datenschutz & KI-Verarbeitung
          </CardTitle>
          <CardDescription>Einstellungen zur KI-gestützten Belegverarbeitung (DSGVO)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div id="section-ocr-ai">
            <AiConsentToggle />
          </div>
          <div className="border-t border-slate-200 pt-4">
            <BankAiConsentToggle />
          </div>
        </CardContent>
      </Card>
      )}
      {!isLex && <InboundEmailSection />}

      {/* Nummernkreise (lex: nur Rechnungen) */}
      <NumberRangesSection onlyCategories={isLex ? ["invoice"] : null} />

        </TabsContent>

        {/* ─── Tab: Steuern (lex: entfällt — wird durch die Kanzlei befüllt) ─── */}
        {!isLex && (
        <TabsContent value="taxes" className="space-y-6 mt-6" data-testid="settings-content-taxes">
      <SteuerStammdatenSection />
      <TaxSettings />
      <CountryInfo />
        </TabsContent>
        )}

        {/* ─── Tab: Plan & Konto ─── */}
        <TabsContent value="design" className="space-y-6 mt-6" data-testid="settings-content-design">
          <InvoiceDesignSection settings={settings} isLex={isLex} user={user} onSaved={(data) => setSettings(data)} />
        </TabsContent>

        <TabsContent value="plan" className="space-y-6 mt-6" data-testid="settings-content-plan">

      {/* Subscription Management (lex: entfällt — es gibt nur einen Plan) */}
      {!isLex && <SubscriptionManagement />}

      {/* AVV – Auftragsverarbeitungsvertrag (Art. 28 DSGVO) */}
      <AVVSection />

      {/* Change Password */}
      <Card className="border-emerald-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-emerald-600">
            <Lock className="h-5 w-5" />
            {t("settings.changePasswordTitle", "Change Password")}
          </CardTitle>
          <CardDescription>
            {t("settings.changePasswordDesc", "Change your login password. You will be logged out after the change.")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button 
                type="button" 
                variant="outline" 
                className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50"
              >
                {t("settings.changePassword", "Change Password")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t("settings.changePasswordTitle", "Change Password")}
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-4">
                  <p className="text-sm text-gray-600">
                    {t("settings.changePasswordInfo", "After the change, you will be logged out and must sign in with your new password.")}
                  </p>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="current-password" className="text-sm font-medium">
                        {t("settings.currentPassword", "Aktuelles Passwort")}
                      </Label>
                      <Input
                        id="current-password"
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                        placeholder={t("settings.enterCurrentPassword", "Aktuelles Passwort eingeben")}
                        className="mt-1"
                        autoComplete="current-password"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-password" className="text-sm font-medium">
                        {t("settings.newPassword", "Neues Passwort")}
                      </Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        placeholder={t("settings.enterNewPassword", "Neues Passwort (min. 8 Zeichen)")}
                        className="mt-1"
                        autoComplete="new-password"
                        minLength={8}
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm-password" className="text-sm font-medium">
                        {t("settings.confirmNewPassword", "Confirm New Password")}
                      </Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        placeholder={t("settings.confirmNewPassword", "Neues Passwort bestätigen")}
                        className="mt-1"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" })}>
                  {t("common.cancel", "Abbrechen")}
                </AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleChangePassword}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                >
                  {t("settings.changePassword", "Change Password")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>

      {/* Sicherheit & Sitzungen */}
      <Card data-testid="security-sessions-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {t("settings.securitySessionsTitle", "Sicherheit & Sitzungen")}
          </CardTitle>
          <CardDescription>
            {t("settings.securitySessionsDesc", "Überblick über deine letzten Anmeldungen. Melde dich bei Verdacht auf einen kompromittierten Zugang auf allen Geräten ab.")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label className="text-sm font-medium">{t("settings.recentLogins", "Letzte Anmeldungen")}</Label>
            <div className="mt-2 divide-y rounded-lg border" data-testid="login-history-list">
              {loginHistory.length === 0 ? (
                <p className="p-3 text-sm text-gray-500" data-testid="login-history-empty">
                  {t("settings.noLoginHistory", "Noch keine Anmeldungen erfasst.")}
                </p>
              ) : (
                loginHistory.map((ev, i) => (
                  <div key={ev.id || i} className="flex items-center justify-between gap-3 p-3" data-testid={`login-history-item-${i}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{formatDevice(ev.user_agent)}</p>
                      <p className="text-xs text-gray-500">{ev.ip || "—"}</p>
                    </div>
                    <span className="shrink-0 text-xs text-gray-500">{formatDate(ev.created_at)}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg bg-amber-50 border border-amber-200 p-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-900">{t("settings.logoutAllTitle", "Auf allen Geräten abmelden")}</p>
              <p className="text-xs text-amber-800">{t("settings.logoutAllDesc", "Beendet sofort alle aktiven Sitzungen (auch auf anderen Geräten). Du musst dich danach neu anmelden.")}</p>
            </div>
            <Button
              variant="outline"
              className="shrink-0 border-amber-300 text-amber-900 hover:bg-amber-100"
              onClick={handleLogoutAllDevices}
              disabled={logoutAllLoading}
              data-testid="logout-all-devices-btn"
            >
              <Lock className="h-4 w-4 mr-2" />
              {logoutAllLoading ? t("settings.loggingOut", "Wird abgemeldet…") : t("settings.logoutAllButton", "Überall abmelden")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Account - Single instance */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" />
            {t("settings.deleteAccountTitle", "Delete Account")}
          </CardTitle>
          <CardDescription>
            {t("settings.deleteAccountDesc", "Permanently delete your account and all associated data.")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button 
                type="button" 
                variant="destructive" 
                className="w-full"
              >
                {t("settings.deleteAccount", "Delete Account")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t("settings.confirmDeleteTitle", "Really delete account?")}
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-3">
                  <p>
                    {t("settings.confirmDeleteDesc", "This action cannot be undone. The following data will be deleted:")}
                  </p>
                  <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                    <li>{t("settings.deleteData1", "All projects and time entries")}</li>
                    <li>{t("settings.deleteData2", "Alle Kunden und Rechnungen")}</li>
                    <li>{t("settings.deleteData3", "Alle Einnahmen und Ausgaben")}</li>
                    <li>{t("settings.deleteData4", "Your Stripe subscription will be cancelled")}</li>
                  </ul>
                  <div className="pt-3">
                    <Label htmlFor="delete-reason" className="text-sm font-medium">
                      {t("settings.deleteReasonLabel", "Warum löschst du dein Konto? (optional)")}
                    </Label>
                    <Input
                      id="delete-reason"
                      data-testid="delete-reason-input"
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      placeholder={t("settings.deleteReasonPlaceholder", "z. B. zu teuer, Funktion fehlt, nutze etwas anderes …")}
                      className="mt-1"
                      maxLength={500}
                    />
                  </div>
                  <div className="pt-3">
                    <Label htmlFor="delete-password" className="text-sm font-medium">
                      {t("settings.confirmPassword", "Password to confirm")}
                    </Label>
                    <Input
                      id="delete-password"
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder={t("settings.enterPassword", "Passwort eingeben")}
                      className="mt-2"
                      autoComplete="current-password"
                    />
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setDeletePassword("")}>
                  {t("common.cancel", "Abbrechen")}
                </AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDeleteAccount}
                  className="bg-red-600 hover:bg-red-700"
                  disabled={!deletePassword}
                >
                  {t("settings.confirmDelete", "Delete Permanently")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>

      {/* Data Export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-blue-600" />
            {t("settings.exportDataTitle")}
          </CardTitle>
          <CardDescription>{t("settings.exportDataDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleDataExport}
            className="w-full"
          >
            {t("settings.exportData")}
          </Button>
        </CardContent>
      </Card>

        </TabsContent>

        {/* ─── Tab: Steuerberater (lex: entfällt — läuft über den Kanzleizugang) ─── */}
        {!isLex && (
        <TabsContent value="advisor" className="space-y-6 mt-6" data-testid="settings-content-advisor">
          <AdvisorAccessSection />
        </TabsContent>
        )}

        {!isLex && (
        <TabsContent value="migration" className="mt-6" data-testid="settings-content-migration">
          <div className="-mx-4 sm:-mx-6 lg:-mx-8" data-testid="settings-migration-embed">
            <Migration />
          </div>
        </TabsContent>
        )}
      </Tabs>

      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        message={upgradeMessage}
        currentPlan={user?.plan || 'free'}
      />
    </div>
  );
}

// ─────────── Sprint 1 (Feb 2026): Steuer-Stammdaten-Sektion ───────────
//
// Zeigt die Tax-Health-Score-Karte + das volle Stammdaten-Formular.
// Bietet einen prominenten "Onboarding starten"-Button als sanfter Trigger.
function SteuerStammdatenSection() {
  const [profile, setProfile] = useState(null);
  const [country, setCountry] = useState(null);
  const [healthRefresh, setHealthRefresh] = useState(0);
  const [loading, setLoading] = useState(true);

  const authHeaders = () => {
    const tok = localStorage.getItem('auth_token') || localStorage.getItem('access_token') || localStorage.getItem('token');
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  };

  const load = async () => {
    setLoading(true);
    try {
      const [pr, cc] = await Promise.all([
        axios.get(`${API_BASE}/me/tax-profile`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/me/country-config`, { headers: authHeaders() }),
      ]);
      setProfile(pr.data);
      setCountry(cc.data);
    } catch (err) {
      // Non-fatal beim ersten Settings-Open: backend kann kurz brauchen oder
      // tax-profile existiert noch nicht. Wir loggen für Debugging.
      console.warn('[Settings.TaxProfile] load failed:', err?.response?.status, err?.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onSave = async (payload) => {
    await axios.put(`${API_BASE}/me/tax-profile`, payload, { headers: authHeaders() });
    await load();
    setHealthRefresh((k) => k + 1);
  };

  if (loading) return null;

  return (
    <div className="space-y-4" data-testid="steuer-stammdaten-section">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <h2 className="text-base font-semibold text-slate-900 mb-1">Steuer-Stammdaten</h2>
          <p className="text-sm text-slate-500">
            Diese Daten teilst du mit deinem Steuerberater. Je vollständiger, desto weniger Rückfragen.
          </p>
        </div>
        <div className="lg:col-span-1">
          <TaxHealthScoreCard refreshKey={healthRefresh} />
        </div>
      </div>
      <TaxProfileForm profile={profile} countryConfig={country} onSave={onSave} />
    </div>
  );
}

